import React from 'react';
import {
  object, bool, array, number, func,
} from 'prop-types';
import cn from 'classnames';
import { IconButton } from '@picsio/ui';
import { Edit } from '@picsio/icons';
import isValidAssetName from '../../../helpers/assets/isValidAssetName';
import ua from '../../../ua';
import * as utils from '../../../shared/utils';
import localization from '../../../shared/strings';
import Logger from '../../../services/Logger';
import showExportCsvDialog from '../../../helpers/showExportCsvDialog';
import showDownloadDialog from '../../../helpers/fileDownloader/showDownloadDialog';
import { Input } from '../../../UIComponents';
import Icon from '../../Icon';
import LockMenu from './LockMenu';
import AssetsMenu from './AssetsMenu';
import { showDialog } from '../../dialog';
import Tooltip from '../../Tooltip';

class Header extends React.Component {
  state = {
    name: '',
    assetNewName: '',
    assetExt: '',
    renaming: false,
    renameInProgress: false,
    error: null,
  };

  static getDerivedStateFromProps(props, state) {
    if (props.collection.length === 1) {
      let newState = { name: props.collection[0].name, error: props.error };
      if (props.collection[0].name !== state.name || state.error !== props.error) {
        /* if name changed */
        newState = {
          ...newState,
          assetNewName: '',
          assetExt: '',
          renaming: false,
          renameInProgress: false,
        };
      }
      return newState;
    }
    return null;
  }

  downloadSelectedImages = () => {
    Logger.log('User', 'InfoPanelDownload');
    showDownloadDialog(this.props.selectedAssetsIds);
  };

  removeSelectedImages = () => {
    Logger.log('User', 'InfoPanelTrash');
    this.props.deleteAll(this.props.selectedAssetsIds, this.props.isRemoveForever);
  };

  initAssetRenaming = () => {
    Logger.log('User', 'InfoPanelRename');
    if (this.props.permissions.fileNameEditable !== true || this.props.collection.length !== 1) { return; }

    const assetNameFull = this.props.collection[0].name.split('.');
    const assetExt = assetNameFull.pop();
    const assetNewName = assetNameFull.join('.');
    this.setState(
      {
        renaming: true,
        assetExt,
        assetNewName,
      },
      () => {
        this.$inputName?.select();
      },
    );
  };

  handleDuplicate = () => {
    const id = this.props.selectedAssetsIds[0];
    this.props.actions.duplicateAssets([id], true, true);
  }

  handleInputRenameBlur = () => {
    const id = this.props.selectedAssetsIds[0];
    const { collection } = this.props;
    const { assetExt } = this.state;
    const assetNewName = this.state.assetNewName.trim();
    const currentModel = collection.find((model) => model._id === id);
    const name = `${assetNewName}.${assetExt}`;

    if (currentModel.name !== name && assetNewName !== '' && isValidAssetName(name, { showToast: true })) {
      /** if name is changed and new name is not empty - do rename */
      this.setState({ renameInProgress: true });
      this.props.actions.rename(id, name);
      return;
    }
    this.setState({ renaming: false });
  };

  handleInputRenameKeyDown = (event) => {
    if (event.keyCode === 13) {
      this.handleInputRenameBlur();
    }

    if (event.keyCode === 27) {
      this.setState({ renaming: false });
    }
  };

  handleInputRenameChange = () => {
    const assetNewName = this.$inputName.value;
    this.setState({ assetNewName });
  };

  refName = (node) => (this.$inputName = node);

  exportToCSV = () => {
    const ASSETS_LIMIT = 65000;
    if (ua.browser.isNotDesktop()) {
      /** Mobile devices is not supported */
      const { title, text, textBtnCancel } = localization.EXPORT_TO_CSV_DIALOG.notAvailable;
      showDialog({
        title,
        text,
        textBtnCancel,
        textBtnOk: null,
      });
      return;
    }
    if (this.props.selectedAssetsIds.length > ASSETS_LIMIT) {
      /** More than ASSETS_LIMIT */
      const { title, text, textBtnCancel } = localization.EXPORT_TO_CSV_DIALOG.assetsLimit;
      showDialog({
        title,
        text: text(utils.formatNumberWithSpaces(ASSETS_LIMIT)),
        textBtnCancel,
        textBtnOk: null,
      });
      return;
    }
    showExportCsvDialog({ assetIds: this.props.selectedAssetsIds, initiationLocation: 'three dots menu' });
  };

  render() {
    const { props, state } = this;
    const {
      collection,
      isRestricted,
      permissions = {},
      isArchived,
      selectedAssetsIds,
      filterGroupsByPermission,
      toggleEditPanel,
      groups,
      isRemoveForever,
    } = props;

    const showAssetMenu = (permissions && permissions.assetsIsDownloadable === true
      && (!isRestricted
        || (isRestricted && permissions.restrictedIsDownloadableOrShareable === true)))
      || (permissions.assetsIsRemovable === true
        && (!isRestricted
          || (isRestricted && permissions.restrictedIsAttachableOrRemovable === true)))
      || (collection.length < 2 && permissions.fileNameEditable === true);

    return (
      <div data-qa="details-header" className="detailsPanel__header  detailsPanel__btns">
        <div className="detailsPanel__statistics">
          {/* filename */}
          <If condition={permissions.fileNameShow}>
            <div
              className={cn('filename', {
                disabled: state.renameInProgress,
                isRenaming: state.renaming,
              })}
              onDoubleClick={this.initAssetRenaming}
            >
              <Choose>
                <When condition={state.renaming && selectedAssetsIds.length === 1}>
                  <Input
                    type="text"
                    className="assetRenaming"
                    error={!isValidAssetName(state.assetNewName)}
                    value={state.assetNewName}
                    onChange={this.handleInputRenameChange}
                    onKeyDown={this.handleInputRenameKeyDown}
                    onBlur={this.handleInputRenameBlur}
                    disabled={state.renameInProgress}
                    customRef={this.refName}
                  />
                </When>
                <Otherwise>
                  <Choose>
                    <When condition={collection.length === 1 && selectedAssetsIds.length === 1}>{collection[0].name}</When>
                    <Otherwise>
                      {localization.DETAILS.textSelectedFiles(
                        utils.formatNumberWithSpaces(props.selectedAssetsIds.length),
                      )}
                    </Otherwise>
                  </Choose>
                </Otherwise>
              </Choose>
            </div>
          </If>
          {/* actions buttons */}
          <If condition={props.isMainApp}>
            <div className="detailsPanel__statistics__btns">
              {/* Rename */}
              <If condition={permissions.fileNameEditable === true && collection.length === 1}>
                <Tooltip content={localization.DETAILS.tooltipRename} placement="top">
                  <IconButton
                    color="default"
                    component="button"
                    id="menuRename"
                    onClick={this.initAssetRenaming}
                    size="lg"
                    disabled={state.renaming}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </If>

              <LockMenu
                currentLock={props.currentLock}
                detailsPanelEditable={props.detailsPanelEditable}
                toggleEditable={props.toggleEditable}
                disabled={props.lockMenuDisabled}
              />

              <If condition={showAssetMenu}>
                <AssetsMenu
                  isRestricted={isRestricted}
                  isRemoveForever={isRemoveForever}
                  removeSelectedImages={this.removeSelectedImages}
                  permissions={permissions}
                  exportToCSV={this.exportToCSV}
                  isArchived={Boolean(isArchived)}
                  filterGroupsByPermission={filterGroupsByPermission}
                  toggleEditPanel={toggleEditPanel}
                  groups={groups}
                />
              </If>
            </div>
          </If>
        </div>
        <If condition={isArchived}>
          <div className="detailsPanel__note">
            <Icon name="archive" />
            <span>{`Asset${props.selectedAssetsIds.length > 1 ? 's are' : ' is'
            } archived, read only`}
            </span>
          </div>
        </If>
      </div>
    );
  }
}

Header.propTypes = {
  filterGroupsByPermission: func,
  toggleEditPanel: func,
  groups: array,
  actions: object,
  isMainApp: bool,
  permissions: object,
  collection: array,
  total: number,
  selectedAssetsIds: array,
  detailsPanelEditable: object,
  currentLock: bool,
  toggleEditable: func,
  selectAll: func,
  deselectAll: func,
  deleteAll: func,
  isDownloadable: bool,
  error: object,
  lockMenuDisabled: bool,
  isRestricted: bool,
  isRemoveForever: bool,
  isArchived: bool,
};

export default Header;
